import { useEffect, useState } from 'react';
import { Modal } from '@sandtech-hub-monorepo/ui-kit';
import { useNavigate } from 'react-router-dom';
import { ProgramEnrollmentConfirmModal } from './ProgramEnrollmentConfirmModal';
import { EnrollInfoModal } from './EnrollInfoModal';
import {
  ApplyToProgramDocument,
  GetViewerAvailableProgramsQuery,
  GetViewerLearningDetailsDocument,
  ViewerQuery,
} from '@sandtech-hub-monorepo/ehub-client';
import { useMutation } from '@apollo/client';
import {
  DataCollectionModal,
  hasCompletedDataCollection,
} from '../../components/data-collection/DataCollectionModal';
import {
  enrolledEvent,
  enrolledParams,
  programParams,
  trackEvent,
  AnalyticsEvent,
} from '@sandtech-hub-monorepo/ui-auth-kit';

type EnrollmentModalStep = 'confirm' | 'data-collection';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  program: NonNullable<
    GetViewerAvailableProgramsQuery['viewer']['availablePrograms']
  >[number];
  learningPageLink: string;
  viewer?: ViewerQuery['viewer'];
}

export const ProgramEnrollmentModal = ({
  open,
  onOpenChange,
  program,
  learningPageLink,
  viewer,
}: Props) => {
  const [modalStep, setModalStep] = useState<EnrollmentModalStep>('confirm');
  const navigate = useNavigate();

  useEffect(() => {
    if (open)
      trackEvent(AnalyticsEvent.EnrollmentStarted, programParams(program));
    // Once per opening, not when the program object is re-fetched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isTestVariant = program.requiresAdditionalQuestionnaire;
  const isDataCollectionComplete = hasCompletedDataCollection(viewer);

  const [applyToProgram] = useMutation(ApplyToProgramDocument, {
    refetchQueries: [
      {
        query: GetViewerLearningDetailsDocument,
      },
    ],
    onCompleted: (data) => {
      const user = data?.applyToProgram?.user;
      trackEvent(enrolledEvent(user), enrolledParams(program, user));
      navigate(learningPageLink);
      onOpenChange(false);
    },
  });

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      // Reset to the first step when modal closes
      setModalStep('confirm');
    }
  };

  const handleConfirmNext = () => {
    // Determine next step after confirm modal
    if (!isDataCollectionComplete) {
      setModalStep('data-collection');
    } else {
      // Both are completed, enroll directly
      handleDataCollectionComplete();
    }
  };

  const handleDataCollectionComplete = async () => {
    // After diversity step, enroll in the program
    await applyToProgram({
      variables: {
        programInput: {
          programId: program.id,
        },
      },
    });
  };

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      {program.requiresTuitionQuestionnaire ? (
        // University program flow
        <EnrollInfoModal
          fee={program.marketing.accessFee}
          name={program.name}
          programId={program.id}
          close={() => onOpenChange(false)}
        />
      ) : isTestVariant && modalStep === 'data-collection' ? (
        // Step 2 - Data collection modal (only in test variant)
        <DataCollectionModal
          viewer={viewer}
          onComplete={handleDataCollectionComplete}
          completionLabel="Continue to enrollment"
        />
      ) : (
        // Non-university program flow: Step 1 - Confirm modal (normal or first step of test variant)
        <ProgramEnrollmentConfirmModal
          id={program.id}
          name={program.name}
          abbreviation={program.abbreviation}
          duration={program.marketing.programDuration || ''}
          commitment={program.marketing.timeCommitment || ''}
          startDate={
            program.potentialProgramJourney?.startDate
              ? new Date(program.potentialProgramJourney.startDate)
              : undefined
          }
          requiresPreferredHubForEnrollment={
            program.requiresPreferredHubForEnrollment
          }
          preferredHubOption={program.preferredHubOption}
          learningPageLink={learningPageLink}
          isTestVariant={isTestVariant}
          onNext={handleConfirmNext}
          allInfoCompleted={isDataCollectionComplete}
        />
      )}
    </Modal>
  );
};
